import React, { useEffect, useState, useCallback } from 'react';
import {
  Box,
  CircularProgress,
  Divider,
  FormControlLabel,
  Checkbox,
  List,
  ListItemButton,
  ListItemText,
  Paper,
  TextField,
  Typography,
  Button,
} from '@mui/material';
import DashboardLayout from '../components/DashboardLayout';
import { apiService } from '../services/api';
import { useAppDispatch } from '../store';
import { showNotification } from '../store/notificationSlice';
import type { UserListItem } from '../types/users';
import type { CustomField } from '../types/customFields';
import type { EmployeeTag } from '../types/employeeTags';

interface DeniableField {
  key: string;
  label: string;
  group: 'Custom Fields' | 'Tags';
}

export default function UserFieldAccess() {
  const dispatch = useAppDispatch();

  const [users, setUsers] = useState<UserListItem[]>([]);
  const [usersLoading, setUsersLoading] = useState(true);
  const [userFilter, setUserFilter] = useState('');

  const [selectedUsername, setSelectedUsername] = useState<string | null>(null);

  const [deniableFields, setDeniableFields] = useState<DeniableField[]>([]);
  const [fieldsLoading, setFieldsLoading] = useState(false);

  const [denied, setDenied] = useState<Set<string>>(new Set());
  const [saving, setSaving] = useState(false);

  // Load users once on mount
  useEffect(() => {
    apiService.listUsers().then((res) => {
      setUsers(res.data ?? []);
    }).catch(() => {
      dispatch(showNotification({ message: 'Failed to load users', severity: 'error' }));
    }).finally(() => setUsersLoading(false));
  }, [dispatch]);

  // Build the list of deniable fields from custom fields + root tags
  const loadDeniableFields = useCallback(async () => {
    setFieldsLoading(true);
    try {
      const [cfRes, tagRes] = await Promise.all([
        apiService.listCustomFields(),
        apiService.listEmployeeTags(),
      ]);
      const fields: DeniableField[] = [];

      (cfRes.data as CustomField[]).forEach((cf) => {
        fields.push({ key: cf.fieldName, label: cf.fieldTitle || cf.fieldName, group: 'Custom Fields' });
        if (cf.allowDualValues) {
          fields.push({ key: `${cf.fieldName}_B`, label: `${cf.fieldTitle || cf.fieldName} (Secondary)`, group: 'Custom Fields' });
        }
      });

      (tagRes.data as EmployeeTag[])
        .filter((t) => t.parentId === 0 && t.tagCode)
        .forEach((t) => {
          fields.push({ key: t.tagCode!, label: t.tagTitle, group: 'Tags' });
        });

      setDeniableFields(fields);
    } catch {
      dispatch(showNotification({ message: 'Failed to load field definitions', severity: 'error' }));
    } finally {
      setFieldsLoading(false);
    }
  }, [dispatch]);

  useEffect(() => {
    loadDeniableFields();
  }, [loadDeniableFields]);

  // Load current denials when a user is selected
  useEffect(() => {
    if (!selectedUsername) {
      setDenied(new Set());
      return;
    }
    apiService.getUserFieldDenials(selectedUsername).then((res) => {
      setDenied(new Set(res.deniedFields ?? []));
    }).catch(() => {
      dispatch(showNotification({ message: 'Failed to load field denials', severity: 'error' }));
    });
  }, [selectedUsername, dispatch]);

  const toggleField = (key: string) => {
    setDenied((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const handleSave = async () => {
    if (!selectedUsername) return;
    setSaving(true);
    try {
      await apiService.setUserFieldDenials(selectedUsername, Array.from(denied));
      dispatch(showNotification({ message: 'Field access saved', severity: 'success' }));
    } catch {
      dispatch(showNotification({ message: 'Failed to save field access', severity: 'error' }));
    } finally {
      setSaving(false);
    }
  };

  const filteredUsers = users.filter((u) =>
    u.username.toLowerCase().includes(userFilter.toLowerCase())
  );

  const groups = ['Custom Fields', 'Tags'] as const;

  return (
    <DashboardLayout>
      <Box sx={{ p: 3 }}>
        <Typography variant="h5" fontWeight={600} mb={3}>
          Field Access Control
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, height: 'calc(100vh - 180px)' }}>
          {/* Left panel — user list */}
          <Paper sx={{ width: 260, flexShrink: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            <Box sx={{ p: 1.5 }}>
              <TextField
                size="small"
                fullWidth
                placeholder="Search users…"
                value={userFilter}
                onChange={(e) => setUserFilter(e.target.value)}
              />
            </Box>
            <Divider />
            {usersLoading ? (
              <Box display="flex" justifyContent="center" p={3}>
                <CircularProgress size={24} />
              </Box>
            ) : (
              <List dense sx={{ overflowY: 'auto', flex: 1 }}>
                {filteredUsers.map((u) => (
                  <ListItemButton
                    key={u.username}
                    selected={selectedUsername === u.username}
                    onClick={() => setSelectedUsername(u.username)}
                  >
                    <ListItemText primary={u.username} secondary={u.email} />
                  </ListItemButton>
                ))}
              </List>
            )}
          </Paper>

          {/* Right panel — field checklist */}
          <Paper sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            {!selectedUsername ? (
              <Box display="flex" alignItems="center" justifyContent="center" flex={1}>
                <Typography color="text.secondary">Select a user to manage field access</Typography>
              </Box>
            ) : fieldsLoading ? (
              <Box display="flex" justifyContent="center" p={4}>
                <CircularProgress />
              </Box>
            ) : (
              <>
                <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Typography fontWeight={600}>{selectedUsername}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Checked = denied (field hidden from this user)
                  </Typography>
                </Box>
                <Box sx={{ flex: 1, overflowY: 'auto', p: 2 }}>
                  {groups.map((group) => {
                    const items = deniableFields.filter((f) => f.group === group);
                    if (items.length === 0) return null;
                    return (
                      <Box key={group} mb={3}>
                        <Typography variant="subtitle2" color="text.secondary" mb={1}>
                          {group}
                        </Typography>
                        <Divider sx={{ mb: 1 }} />
                        {items.map((f) => (
                          <FormControlLabel
                            key={f.key}
                            label={f.label}
                            control={
                              <Checkbox
                                size="small"
                                checked={denied.has(f.key)}
                                onChange={() => toggleField(f.key)}
                              />
                            }
                          />
                        ))}
                      </Box>
                    );
                  })}
                  {deniableFields.length === 0 && (
                    <Typography color="text.secondary">No deniable fields configured yet.</Typography>
                  )}
                </Box>
                <Box sx={{ p: 2, borderTop: '1px solid', borderColor: 'divider', display: 'flex', justifyContent: 'flex-end' }}>
                  <Button
                    variant="contained"
                    onClick={handleSave}
                    disabled={saving}
                  >
                    {saving ? 'Saving…' : 'Save'}
                  </Button>
                </Box>
              </>
            )}
          </Paper>
        </Box>
      </Box>
    </DashboardLayout>
  );
}
